import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonTesting = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Unit Testing with Pytest</h1>
                <p className="subtitle">Stop hoping your code works. Prove it. Mastering TDD, Fixtures, and Mocks.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine you build a car.
                    </p>
                    <ul>
                        <li><strong>Manual Testing:</strong> You drive it yourself into a wall to see if the airbag works. If it fails, you die (Production Crash).</li>
                        <li><strong>Automated Testing:</strong> You put a <strong>Crash Test Dummy</strong> in the car and smash it into a wall in a simulator. You do this 1,000 times before a real human ever sits inside.</li>
                        <li><strong>The Goal:</strong> Catch bugs in the simulator (Dev Environment), not on the highway (Production).</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. Why Pytest? (The Industry Standard)</h2>
                <p>
                    Python has a built-in `unittest` module (based on Java's JUnit). It requires Classes and Boilerplate.
                    <strong>Pytest</strong> is modern, functional, and requires zero boilerplate.
                </p>
                <CodeBlock language="python" code={`# 1. Standard Python Function (The Code)
def add(a, b):
    return a + b

# 2. Pytest Function (The Test)
# Just start the function name with 'test_'
def test_add_simple():
    result = add(2, 3)
    assert result == 5 # No self.assertEqual() needed!

def test_add_negative():
    assert add(-1, -1) == -2`} />
            </div>

            <div className="content-section">
                <h2>3. Fixtures: The Setup & Teardown Magic</h2>
                <p>
                    Real apps need data (Databases, User Objects). creating them in every test is repetitive.
                    <strong>Fixtures</strong> let you define setup logic once and inject it into any test.
                </p>
                <CodeBlock language="python" code={`import pytest

# Define a fixture using the decorator
@pytest.fixture
def dummy_user():
    return {"id": 1, "username": "admin", "role": "superuser"}

# Inject it by name!
def test_superuser_permissions(dummy_user):
    # Pytest automatically calls dummy_user() and passes the result
    assert dummy_user["role"] == "superuser"`} />
                <p>
                    Fixtures can also handle <strong>Teardown</strong> (Cleanup) using `yield`.
                </p>
                <CodeBlock language="python" code={`@pytest.fixture
def db_connection():
    print(" Connecting to DB...")
    db = connect_to_db()
    
    yield db # Test runs here!
    
    print(" Closing DB connection...")
    db.close()`} />
            </div>

            <div className="content-section">
                <h2>4. Deep Dive: TDD (Test Driven Development)</h2>
                <p>
                    TDD is a philosophy: <strong>Write the Test BEFORE the Code.</strong>
                </p>
                <ol className="premium-list">
                    <li><strong>Red:</strong> Write a test for a feature that doesn't exist yet. Run it. It fails (Red).</li>
                    <li><strong>Green:</strong> Write the <em>minimum</em> amount of code to make the test pass.</li>
                    <li><strong>Refactor:</strong> Clean up the code while ensuring the test stays Green.</li>
                </ol>
                <ProTip><p><strong>Benefit:</strong> This forces you to design the API usage before implementation, resulting in much cleaner, more usable interfaces.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>5. Mocking: Faking the Outside World</h2>
                <p>
                    What if your function calls a Credit Card API? You can't charge a real card every time you run tests!
                    You use a <strong>Mock</strong> to fake the response.
                </p>
                <CodeBlock language="python" code={`from unittest.mock import patch
import requests
import { ProTip } from '../../components/LessonUI';

def get_exchange_rate():
    # Real network call
    resp = requests.get("https://api.forex.com/usd") 
    return resp.json()["rate"]

# The Test
# We patch 'requests.get' so it never hits the internet
@patch("requests.get")
def test_get_exchange_rate(mock_get):
    # 1. Setup the fake return value
    mock_get.return_value.json.return_value = {"rate": 1.5}
    
    # 2. Run code
    rate = get_exchange_rate()
    
    # 3. Verify
    assert rate == 1.5
    mock_get.assert_called_once() # Ensure we actually called the API`} />
            </div>

            <div className="content-section">
                <h2>6. Parametrization: Running 1 Test 100 Times</h2>
                <p>
                    Don't write 10 copy-pasted tests. Use `@pytest.mark.parametrize` to run the same logic with different inputs.
                </p>
                <CodeBlock language="python" code={`@pytest.mark.parametrize("input, expected", [
    ("hello", "HELLO"),
    ("world", "WORLD"),
    ("Python", "PYTHON"),
    ("", "")
])
def test_str_upper(input, expected):
    assert input.upper() == expected`} />
            </div>

        </div>
    );
};

export default PythonTesting;

